import {
	type ChangeEvent,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import type { WallOpening } from "../../domain/framing/generateFramedWall";
import type { InteriorLayout } from "../../domain/interior";
import {
	DEFAULT_SAUNA_ROTATION_DEG,
	type SaunaRotationDeg,
	stepRotationDeg,
} from "../../domain/viewer/saunaRotation";
import { normalizeTimeOfDay } from "../../domain/viewer/timeOfDay";
import type {
	BuildInput,
	SaunaEngine,
	VisibilityFlags,
} from "../../engines/types";
import { useIsMobile } from "../../hooks/useIsMobile";
import { useT } from "../../i18n";
import { useUnitSystem } from "../../units/UnitSystemContext";
import type { InteriorDragPreview } from "./InteriorDragController";
import { VisibilityBar } from "./VisibilityBar";
import { SceneCanvas } from "./SceneCanvas";
import type { ScaleFigureCapacity } from "./scaleFigureLayout";
import {
	resolveSectionLegendEntries,
	type SectionAxis,
	type SectionLayerFamily,
} from "./sectionLayers";
import { useViewerAmbience } from "./useViewerAmbience";
import type {
	MeasurementHoverState,
	MeasurementTarget,
} from "./ViewerMeasurementController";
import { ViewerPlacementBanner } from "./ViewerPlacementBanner";
import { isViewerAmbienceSupported } from "./viewerAmbience";
import { resolveCameraPresets } from "./viewerCameraPresets";
import type { SceneVisibility } from './sceneVisibility';
import type {
	PresentationMode,
	TimeOfDayValue,
	ViewMode,
} from "./viewerConfig";
import {
	type AdaptiveViewerQualityState,
	applyCinematicPresentation,
	getViewerPresentationProfile,
	getViewerRuntimeProfile,
	resolveAdaptiveViewerQualityState,
	resolveViewerQualityTier,
	type ViewerPerformanceSample,
	type ViewerQualityTier,
} from "./viewerConfig";
import {
	type PartitionMeasurementPreview,
	resolveInteriorDragReadout,
	resolveMeasurementReadout,
	resolvePartitionPreviewReadout,
} from "./viewerMeasurements";
import type { ViewerSnapshotOptions } from "./viewerSnapshot";

const VIEWER_RECOVERY_TIMEOUT_MS = 5000;
const SCENE_READY_CONFIRMATION_MS = 620;
const MOBILE_HELP_QUERY = "(max-width: 768px)";
// Sound is a device-local viewer preference, not design data: it never reaches
// the store, undo/redo, autosave or a share link — same contract as the unit
// system (see docs/notes/2026-06-14-viewer-time-of-day-state-crosses-persistence.md).
const AMBIENCE_STORAGE_KEY = "bb-viewer-ambience";
// The help panel is a first-run orientation aid, not a persistent overlay.
// On desktop it auto-collapses to the `?` button after this idle window so it
// stops occluding the model; a canvas drag/zoom collapses it sooner.
const HELP_AUTO_COLLAPSE_MS = 6000;
type ViewerRendererState = "ready" | "recovering" | "failed";
type ViewerSceneLoadState = "loading" | "ready" | "hidden";

function SaunaSceneLoadingIndicator({
	state,
	loadingLabel,
	readyLabel,
}: {
	state: Exclude<ViewerSceneLoadState, "hidden">;
	loadingLabel: string;
	readyLabel: string;
}) {
	return (
		<div
			className={`viewer-scene-loading${state === "ready" ? " viewer-scene-loading--ready" : ""}`}
			data-test="viewer-scene-loading"
			role="status"
			aria-live="polite"
			aria-atomic="true"
		>
			<div className="viewer-scene-loading__content">
				<svg
					className="viewer-scene-loading__art"
					viewBox="0 0 120 104"
					aria-hidden="true"
				>
					<ellipse
						className="viewer-scene-loading__glow"
						cx="60"
						cy="79"
						rx="43"
						ry="13"
					/>
					<path
						className="viewer-scene-loading__steam viewer-scene-loading__steam--left"
						d="M38 53c-9-9 6-15-1-25"
					/>
					<path
						className="viewer-scene-loading__steam viewer-scene-loading__steam--middle"
						d="M60 48c-9-10 7-16 0-29"
					/>
					<path
						className="viewer-scene-loading__steam viewer-scene-loading__steam--right"
						d="M82 53c-8-9 6-15 0-25"
					/>
					<path
						className="viewer-scene-loading__stone viewer-scene-loading__stone--back"
						d="M38 73c0-10 8-18 18-18h8c10 0 18 8 18 18v9H38z"
					/>
					<path
						className="viewer-scene-loading__stone viewer-scene-loading__stone--left"
						d="M19 82c0-12 8-21 20-21h5c9 0 15 8 15 18v8H19z"
					/>
					<path
						className="viewer-scene-loading__stone viewer-scene-loading__stone--right"
						d="M58 87v-8c0-11 8-18 18-18h5c12 0 20 9 20 21v5z"
					/>
				</svg>
				<p>{state === "ready" ? readyLabel : loadingLabel}</p>
			</div>
		</div>
	);
}

interface Viewer3DProps {
	buildInput: BuildInput;
	visibility: VisibilityFlags;
	engine: SaunaEngine;
	onVisibilityChange?: (visibility: VisibilityFlags) => void;
	onSnapshotReady?: (
		getSnapshot: (options?: ViewerSnapshotOptions) => string | null,
	) => void;
	timeOfDay?: TimeOfDayValue;
	onTimeOfDayChange?: (timeOfDay: TimeOfDayValue) => void;
	/** When set, start 3D drag placement for a window of this size */
	isDraggingWindow?: { widthMm: number; heightMm: number } | null;
	/** Viewer should only show opening guides while the openings editor is active */
	isEditingOpenings?: boolean;
	/** Called when a window is successfully placed via drag-and-drop */
	onWindowPlaced?: (opening: WallOpening) => void;
	/** Called when an existing opening is moved in the 3D scene */
	onOpeningMoved?: (opening: WallOpening) => void;
	/** Called when the two-room partition split changes via drag */
	onPartitionSplitChange?: (partitionSplit: number) => void;
	/**
	 * Called when a lav or the aggregat is dragged in the model — i.e.
	 * `setInteriorLayout`, the SAME action the plan view commits through. Exactly
	 * once per released drag. Omit it and the interior handles never mount.
	 */
	onInteriorLayoutChange?: (layout: InteriorLayout) => void;
	onSelectBench?: (runId: string | null) => void;
	/** Whole-building orientation on the plot, in degrees (0/90/180/270) */
	rotationDeg?: SaunaRotationDeg;
	/** Called when the building is turned — handle drag or HUD stepper */
	onRotationChange?: (rotationDeg: number) => void;
	/** Called when drag mode is cancelled (Escape or invalid) */
	onDragCancelled?: () => void;
	/**
	 * Upper bound on the scale-figure "sittplatser" count, from the wizard's
	 * size-tier choice ("2"/"4"/"6" personer). That choice never becomes
	 * engine geometry, so without this hint the viewer's bench-length
	 * estimate can land above what the wizard promised.
	 */
	scaleFigureCapacityHint?: number | null;
}

function BlueprintIcon() {
	return (
		<svg
			viewBox="0 0 24 24"
			width="16"
			height="16"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden="true"
		>
			<polygon points="12 2 2 7 12 12 22 7 12 2" />
			<polyline points="2 17 12 22 22 17" />
			<polyline points="2 12 12 17 22 12" />
		</svg>
	);
}

/**
 * Quarter-turn arrow. Mirrored for the two directions so the pair reads as
 * one control rather than two unrelated buttons.
 */
function RotateIcon({ direction }: { direction: "cw" | "ccw" }) {
	return (
		<svg
			viewBox="0 0 24 24"
			width="16"
			height="16"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden="true"
			style={direction === "cw" ? { transform: "scaleX(-1)" } : undefined}
		>
			<path d="M4 12a8 8 0 1 1 3 6.24" />
			<polyline points="4 6 4 12 10 12" />
		</svg>
	);
}

function CinemaIcon() {
	return (
		<svg
			viewBox="0 0 24 24"
			width="16"
			height="16"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden="true"
		>
			<rect x="2" y="6" width="20" height="12" rx="1.5" />
			<circle cx="6" cy="9" r="0.8" fill="currentColor" />
			<circle cx="6" cy="15" r="0.8" fill="currentColor" />
			<circle cx="18" cy="9" r="0.8" fill="currentColor" />
			<circle cx="18" cy="15" r="0.8" fill="currentColor" />
			<line x1="2" y1="12" x2="22" y2="12" />
		</svg>
	);
}

function SectionIcon() {
	return (
		<svg
			viewBox="0 0 24 24"
			width="16"
			height="16"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden="true"
		>
			<rect x="3" y="4" width="18" height="16" rx="1.5" />
			<line x1="12" y1="4" x2="12" y2="20" />
			<line x1="12" y1="9" x2="21" y2="9" />
			<line x1="12" y1="14" x2="21" y2="14" />
		</svg>
	);
}

function ScaleFigureIcon() {
	return (
		<svg
			viewBox="0 0 24 24"
			width="16"
			height="16"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden="true"
		>
			<circle cx="9" cy="5" r="2.4" />
			<path d="M9 8.4v6.1h6.2" />
			<path d="M15.2 14.5v5" />
			<path d="M4 19.5h16" />
		</svg>
	);
}

function SoundIcon({ muted }: { muted: boolean }) {
	return (
		<svg
			viewBox="0 0 24 24"
			width="16"
			height="16"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden="true"
		>
			<path d="M4 9.5h3L11.5 6v12L7 14.5H4z" />
			{muted ? (
				<>
					<line x1="16" y1="9.5" x2="20.5" y2="14.5" />
					<line x1="20.5" y1="9.5" x2="16" y2="14.5" />
				</>
			) : (
				<>
					<path d="M15.5 9.6a3.4 3.4 0 0 1 0 4.8" />
					<path d="M18.4 7.2a7 7 0 0 1 0 9.6" />
				</>
			)}
		</svg>
	);
}

function hexColor(value: number): string {
	return `#${value.toString(16).padStart(6, "0")}`;
}

/**
 * SSR-safe, private-mode-safe localStorage access. Five call sites in this
 * file need the same two guards (no `window` during prerender, and Safari
 * private mode throws on access), so they share one pair of helpers rather
 * than each carrying its own try/catch.
 *
 * A repo-wide `safeLocalStorage` would be the right home - `buildProgress-
 * LocalStore`, `builderIntroState` and `UnitSystemContext` each roll their
 * own - but that consolidation reaches well beyond this file.
 */
function readStoredFlag(key: string): string | null {
	if (typeof window === "undefined") return null;
	try {
		return window.localStorage.getItem(key);
	} catch {
		return null;
	}
}

function writeStoredFlag(key: string, value: string): void {
	if (typeof window === "undefined") return;
	try {
		window.localStorage.setItem(key, value);
	} catch {
		// storage unavailable (private mode / quota) — keep the in-memory value
	}
}

function readStoredAmbiencePreference(): boolean {
	return readStoredFlag(AMBIENCE_STORAGE_KEY) === "1";
}

function HelpIcon() {
	return (
		<svg
			viewBox="0 0 24 24"
			width="16"
			height="16"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden="true"
		>
			<circle cx="12" cy="12" r="9" />
			<path d="M9.5 9a2.5 2.5 0 0 1 5 0c0 1.5-2.5 2-2.5 4" />
			<line x1="12" y1="17" x2="12" y2="17.01" />
		</svg>
	);
}

function ChevronIcon({ className }: { className?: string }) {
	return (
		<svg
			className={className}
			width="12"
			height="12"
			viewBox="0 0 12 12"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.6"
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden="true"
		>
			<path d="M3 4.5L6 7.5L9 4.5" />
		</svg>
	);
}

export function Viewer3D({
	buildInput,
	visibility,
	onVisibilityChange,
	engine,
	onSnapshotReady,
	timeOfDay = 0,
	onTimeOfDayChange = () => {},
	isDraggingWindow,
	isEditingOpenings = false,
	onWindowPlaced,
	onOpeningMoved,
	onPartitionSplitChange,
	onInteriorLayoutChange,
	onSelectBench,
	rotationDeg = DEFAULT_SAUNA_ROTATION_DEG,
	onRotationChange,
	onDragCancelled,
	scaleFigureCapacityHint,
}: Viewer3DProps) {
	const t = useT();
	const { unitSystem } = useUnitSystem();
	const [qualityOverride, setQualityOverride] =
		useState<ViewerQualityTier>("auto");
	const [forcedQualityOverride, setForcedQualityOverride] =
		useState<ViewerQualityTier | null>(null);
	const [adaptiveQualityTier, setAdaptiveQualityTier] =
		useState<AdaptiveViewerQualityState | null>(null);
	const [activePreset, setActivePreset] = useState<
		"hero" | "side" | "interior-peek"
	>("hero");
	const [showQualityPanel, setShowQualityPanel] = useState(false);
	const [viewMode, setViewMode] = useState<ViewMode>("realistic");
	// Section view (cutaway) — transient viewer state only, never stored,
	// so undo/redo and share links are unaffected.
	const [sectionActive, setSectionActive] = useState(false);
	const [sectionAxis, setSectionAxis] = useState<SectionAxis>("x");
	const [sectionPosition, setSectionPosition] = useState(0.5);
	const [sectionFamilies, setSectionFamilies] = useState<SectionLayerFamily[]>(
		[],
	);
	// Section legend + note collapse on mobile so the panel stays a thin control
	// strip over the canvas; on desktop it defaults open like the inline heading.
	const [isSectionInfoOpen, setIsSectionInfoOpen] = useState(() => {
		try {
			if (
				typeof window.matchMedia === "function" &&
				window.matchMedia(MOBILE_HELP_QUERY).matches
			) {
				return false;
			}
		} catch {
			// fall through to default-open
		}
		return true;
	});
	// Scale figures are a presentation aid, not part of the design: pure
	// React state, so they never reach the store, undo/redo, autosave or a
	// share link (see docs/notes/2026-06-14-viewer-time-of-day-state-crosses-persistence.md).
	const [showScaleFigures, setShowScaleFigures] = useState(false);
	const [scaleFigureCapacity, setScaleFigureCapacity] =
		useState<ScaleFigureCapacity | null>(null);
	// Feature detection in the initializer keeps it SSR-safe and re-evaluated
	// per mount (cf. the `navigator.share` detection in the share dialog).
	const [isAmbienceSupported] = useState(isViewerAmbienceSupported);
	const [isAmbienceOn, setIsAmbienceOn] = useState(
		readStoredAmbiencePreference,
	);
	const [isTimeOfDayScrubbing, setIsTimeOfDayScrubbing] = useState(false);
	const [presentationMode, setPresentationMode] =
		useState<PresentationMode>("standard");
	const [rendererState, setRendererState] =
		useState<ViewerRendererState>("ready");
	const [sceneLoadState, setSceneLoadState] =
		useState<ViewerSceneLoadState>("loading");
	const [canvasInstanceKey, setCanvasInstanceKey] = useState(0);
	const isMobile = useIsMobile();
	const [hasInteractedWithHelp, setHasInteractedWithHelp] = useState(false);
	const [isHelpOpen, setIsHelpOpen] = useState(() => {
		try {
			if (
				typeof window.matchMedia === "function" &&
				window.matchMedia(MOBILE_HELP_QUERY).matches
			) {
				return false;
			}
			return !(
				readStoredFlag("bb-viewer-help-seen") ??
				readStoredFlag("bb-viewer-hint-seen")
			);
		} catch {
			return true;
		}
	});
	const [hoveredMeasurement, setHoveredMeasurement] =
		useState<MeasurementHoverState | null>(null);
	const [lockedMeasurement, setLockedMeasurement] =
		useState<MeasurementTarget | null>(null);
	const [partitionDragPreview, setPartitionDragPreview] =
		useState<PartitionMeasurementPreview | null>(null);
	const handleMeasurementSelection = useCallback(
		(target: MeasurementTarget | null) => {
			setLockedMeasurement(target);
			// A freshly mounted canvas clears its measurement; preserve sidebar selection.
			if (target) onSelectBench?.(
				target.kind === "bench" || target.kind === "bench-edge" ? target.runId : null,
			);
		},
		[onSelectBench],
	);

	// Interior direct manipulation (issue 133). Transient VIEWER state: the mode,
	// like the section cut and the scale figures, never reaches the store, undo/redo
	// or a share link — only the committed layout does.
	const [interiorEditing, setInteriorEditing] = useState(false);
	const [interiorEditAvailable, setInteriorEditAvailable] = useState(true);
	const [interiorDragPreview, setInteriorDragPreview] =
		useState<InteriorDragPreview | null>(null);
	const recoveryTimeoutRef = useRef<number | null>(null);
	const sceneReadyTimeoutRef = useRef<number | null>(null);
	const hasCompletedInitialSceneLoadRef = useRef(false);
	const rendererStateRef = useRef<ViewerRendererState>("ready");
	const recoveryAttemptCountRef = useRef(0);
	const rootRef = useRef<HTMLDivElement | null>(null);

	const clearRecoveryTimeout = useCallback(() => {
		if (recoveryTimeoutRef.current !== null) {
			window.clearTimeout(recoveryTimeoutRef.current);
			recoveryTimeoutRef.current = null;
		}
	}, []);

	const clearSceneReadyTimeout = useCallback(() => {
		if (sceneReadyTimeoutRef.current !== null) {
			window.clearTimeout(sceneReadyTimeoutRef.current);
			sceneReadyTimeoutRef.current = null;
		}
	}, []);

	useEffect(() => {
		rendererStateRef.current = rendererState;
	}, [rendererState]);

	useEffect(() => {
		if (!isMobile || hasInteractedWithHelp || !isHelpOpen) return;
		setIsHelpOpen(false);
	}, [hasInteractedWithHelp, isHelpOpen, isMobile]);

	useEffect(
		() => () => {
			clearRecoveryTimeout();
			clearSceneReadyTimeout();
		},
		[clearRecoveryTimeout, clearSceneReadyTimeout],
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: `canvasInstanceKey` is a remount signal (bumped when the canvas is recreated after a context loss), not a value read in the body — it re-arms the recovery timeout for the fresh instance.
	useEffect(() => {
		clearRecoveryTimeout();
		if (rendererState !== "recovering") return;
		recoveryTimeoutRef.current = window.setTimeout(() => {
			setRendererState("failed");
		}, VIEWER_RECOVERY_TIMEOUT_MS);
	}, [canvasInstanceKey, clearRecoveryTimeout, rendererState]);

	const beginRecovery = useCallback(() => {
		clearRecoveryTimeout();
		setRendererState("recovering");
		setForcedQualityOverride("low");
		setCanvasInstanceKey((prev) => prev + 1);
		recoveryAttemptCountRef.current += 1;
	}, [clearRecoveryTimeout]);

	const handleRendererContextLost = useCallback(() => {
		clearRecoveryTimeout();
		if (rendererStateRef.current === "recovering") {
			setRendererState("failed");
			return;
		}
		if (recoveryAttemptCountRef.current === 0) {
			beginRecovery();
			return;
		}
		setRendererState("failed");
	}, [beginRecovery, clearRecoveryTimeout]);

	const handleRendererReady = useCallback(() => {
		clearRecoveryTimeout();
		setRendererState("ready");
	}, [clearRecoveryTimeout]);

	const handleSceneFrameReady = useCallback(() => {
		if (hasCompletedInitialSceneLoadRef.current) return;
		hasCompletedInitialSceneLoadRef.current = true;
		clearSceneReadyTimeout();
		setSceneLoadState("ready");
		sceneReadyTimeoutRef.current = window.setTimeout(() => {
			setSceneLoadState("hidden");
			sceneReadyTimeoutRef.current = null;
		}, SCENE_READY_CONFIRMATION_MS);
	}, [clearSceneReadyTimeout]);

	const handleRetryViewer = useCallback(() => {
		beginRecovery();
	}, [beginRecovery]);

	const handleReloadPage = useCallback(() => {
		window.location.reload();
	}, []);

	const effectiveQualityOverride = forcedQualityOverride ?? qualityOverride;

	const baseQualityTier = useMemo(
		() =>
			resolveViewerQualityTier({
				devicePixelRatio: window.devicePixelRatio || 1,
				viewportWidth: window.innerWidth,
				viewportHeight: window.innerHeight,
				override: effectiveQualityOverride,
			}),
		[effectiveQualityOverride],
	);

	const runtimeProfile = useMemo(() => {
		const tier =
			effectiveQualityOverride === "auto"
				? adaptiveQualityTier?.baseTier === baseQualityTier
					? adaptiveQualityTier.tier
					: baseQualityTier
				: baseQualityTier;
		const base = getViewerRuntimeProfile(tier);
		return presentationMode === "cinematic"
			? applyCinematicPresentation(base)
			: base;
	}, [
		adaptiveQualityTier,
		baseQualityTier,
		effectiveQualityOverride,
		presentationMode,
	]);

	const presentationProfile = useMemo(
		() => getViewerPresentationProfile(runtimeProfile.tier),
		[runtimeProfile.tier],
	);
	const effectiveQualityLabel =
		runtimeProfile.tier === "high"
			? t.viewer_qualityHigh
			: runtimeProfile.tier === "balanced"
				? t.viewer_qualityBalanced
				: t.viewer_qualityLow;

	const handlePerformanceChange = useCallback(
		(performance: ViewerPerformanceSample) => {
			if (effectiveQualityOverride !== "auto") return;
			setAdaptiveQualityTier((current) =>
				resolveAdaptiveViewerQualityState({
					baseTier: baseQualityTier,
					current,
					performance,
				}),
			);
		},
		[baseQualityTier, effectiveQualityOverride],
	);

	// Presets are model-relative by design: "SIDA" must keep meaning the same
	// facade after the building is turned, so the preset rides the rotation.
	const cameraPresets = useMemo(
		() =>
			resolveCameraPresets(engine, buildInput, buildInput.market, rotationDeg),
		[engine, buildInput, rotationDeg],
	);

	const activePresetObj = useMemo(
		() => cameraPresets.find((p) => p.id === activePreset) ?? cameraPresets[0],
		[cameraPresets, activePreset],
	);

	const handleCameraPresetClick = useCallback(
		(presetId: "hero" | "side" | "interior-peek") => setActivePreset(presetId),
		[],
	);

	// Collapse the help panel to its `?` button and mark it seen so it stays
	// collapsed on later visits — used by the manual toggle, the idle timer,
	// and the first canvas-interaction listener alike.
	const collapseHelp = useCallback(() => {
		setIsHelpOpen((prev) => {
			if (!prev) return prev;
			writeStoredFlag("bb-viewer-help-seen", "1");
			return false;
		});
	}, []);

	const toggleHelp = useCallback(() => {
		setHasInteractedWithHelp(true);
		setIsHelpOpen((prev) => {
			const next = !prev;
			if (!next) {
				writeStoredFlag("bb-viewer-help-seen", "1");
			}
			return next;
		});
	}, []);

	// Auto-collapse the open help panel after an idle window so it does not
	// keep occluding the model. Skip on mobile: there the panel starts
	// collapsed and only opens on explicit user action, so an auto-timer
	// would just fight the user. A plain setTimeout (no useFrame) keeps this
	// off the demand render loop.
	useEffect(() => {
		if (isMobile || !isHelpOpen) return;
		const id = window.setTimeout(collapseHelp, HELP_AUTO_COLLAPSE_MS);
		return () => window.clearTimeout(id);
	}, [collapseHelp, isHelpOpen, isMobile]);

	// Collapse on the first canvas drag/zoom: once the user starts moving the
	// camera they are done reading the hint. Listen on the viewer root in the
	// capture phase and only react to events originating on the <canvas> so
	// clicks inside the help panel / HUD chrome don't count as interaction.
	// Pure DOM listeners on an existing element — no new per-frame loop.
	useEffect(() => {
		if (isMobile || !isHelpOpen) return;
		const root = rootRef.current;
		if (!root) return;
		const onCanvasInteract = (event: Event) => {
			const target = event.target as HTMLElement | null;
			if (target?.tagName === "CANVAS") collapseHelp();
		};
		root.addEventListener("pointerdown", onCanvasInteract, { capture: true });
		root.addEventListener("wheel", onCanvasInteract, {
			capture: true,
			passive: true,
		});
		return () => {
			root.removeEventListener("pointerdown", onCanvasInteract, {
				capture: true,
			});
			root.removeEventListener("wheel", onCanvasInteract, { capture: true });
		};
	}, [collapseHelp, isHelpOpen, isMobile]);

	const handleTimeOfDayInput = useCallback(
		(event: ChangeEvent<HTMLInputElement>) => {
			onTimeOfDayChange(normalizeTimeOfDay(event.currentTarget.valueAsNumber));
		},
		[onTimeOfDayChange],
	);

	const stopTimeOfDayScrubbing = useCallback(() => {
		setIsTimeOfDayScrubbing(false);
	}, []);

	const toggleViewMode = useCallback(() => {
		setViewMode((prev) => {
			const next = prev === "realistic" ? "blueprint" : "realistic";
			// Blueprint and section both stash/restore materials — keep them
			// mutually exclusive so the swaps don't clobber each other.
			if (next === "blueprint") setSectionActive(false);
			return next;
		});
	}, []);

	const toggleSection = useCallback(() => {
		setSectionActive((prev) => {
			const next = !prev;
			if (next) setViewMode("realistic");
			return next;
		});
	}, []);

	const handleSectionAxisChange = useCallback((nextAxis: SectionAxis) => {
		setSectionAxis(nextAxis);
		// Re-centre the cut so switching axis doesn't leave the plane off
		// the model along the new direction.
		setSectionPosition(0.5);
	}, []);

	const handleSectionPositionInput = useCallback(
		(event: ChangeEvent<HTMLInputElement>) => {
			setSectionPosition(event.currentTarget.valueAsNumber);
		},
		[],
	);

	const sectionLegendEntries = useMemo(
		() => resolveSectionLegendEntries(sectionFamilies),
		[sectionFamilies],
	);

	const toggleScaleFigures = useCallback(() => {
		setShowScaleFigures((prev) => !prev);
	}, []);

	const persistAmbiencePreference = useCallback((next: boolean) => {
		writeStoredFlag(AMBIENCE_STORAGE_KEY, next ? "1" : "0");
	}, []);

	const toggleAmbience = useCallback(() => {
		setIsAmbienceOn((prev) => {
			const next = !prev;
			persistAmbiencePreference(next);
			return next;
		});
	}, [persistAmbiencePreference]);

	// If the browser refuses to start audio, drop the toggle rather than leave
	// it claiming a sound that is not playing.
	const handleAmbienceUnavailable = useCallback(() => {
		setIsAmbienceOn(false);
		persistAmbiencePreference(false);
	}, [persistAmbiencePreference]);

	// Runs entirely outside the R3F canvas: WebAudio keeps its own clock, so
	// the demand frameloop is never pinned by the ambience.
	useViewerAmbience({
		enabled: isAmbienceSupported && isAmbienceOn,
		heaterType: buildInput.thermalInputs.heaterType,
		timeOfDay,
		onUnavailable: handleAmbienceUnavailable,
	});

	const togglePresentationMode = useCallback(() => {
		setPresentationMode((prev) =>
			prev === "cinematic" ? "standard" : "cinematic",
		);
	}, []);

	const toggleInteriorEditing = useCallback(() => {
		setInteriorEditing((prev) => {
			// A live preview belongs to a gesture that can no longer be released.
			if (prev) setInteriorDragPreview(null);
			// Optimistic: the canvas answers once the mode is on, and assuming yes
			// avoids resolving the layout on every build input just to grey out one
			// button. Reset on re-entry so a design change is re-asked.
			else setInteriorEditAvailable(true);
			return !prev;
		});
	}, []);

	/**
	 * The canvas' answer to "does this design have a layout-driven interior?".
	 *
	 * A `false` has to CLOSE the mode, not merely grey the button out. Entering is
	 * optimistic, so on a barrel the first click hid the front wall and mounted no
	 * handles — and a disabled toggle then left no way back out short of a reload.
	 */
	const handleInteriorEditAvailable = useCallback((available: boolean) => {
		setInteriorEditAvailable(available);
		if (!available) {
			setInteriorEditing(false);
			setInteriorDragPreview(null);
		}
	}, []);

	/**
	 * A different design is a different answer.
	 *
	 * `available: false` disables the toggle, and a disabled button cannot run the
	 * optimistic reset inside `toggleInteriorEditing` — so switching from a barrel
	 * (or any input that could not be resolved) to a supported design left the
	 * feature dead until `Viewer3D` remounted. Nothing can be known about a design
	 * that has not been asked about, so each new one starts from "assume yes".
	 */
	useEffect(() => {
		setInteriorEditAvailable(true);
	}, [buildInput, engine]);

	/**
	 * Is the interior drag mode LIVE?
	 *
	 * ONE predicate, used by both consumers: the front-wall override below and the
	 * `interiorEditing` prop the canvas mounts handles from. Two predicates meant
	 * that placing a window stripped the front wall while mounting no handles.
	 */
	const interiorEditActive =
		interiorEditing && !sectionActive && !isDraggingWindow;

	/** Interior inspection and editing temporarily open the room. Stored layer
	 * preferences return when the mode closes; the layer menu uses this same view. */
	const effectiveVisibility = useMemo<SceneVisibility>(
		() =>
			interiorEditActive || activePreset === "interior-peek"
				? { ...visibility, frontWall: false, interior: true,
					dividerWall: !cameraPresets.some(p => p.id === 'interior-peek' && p.hideDivider) }
				: visibility,
		[activePreset, cameraPresets, interiorEditActive, visibility],
	);

	const handleLayerToggle = (key: keyof VisibilityFlags) => {
		// A manual layer choice leaves any mode that forces that layer on/off.
		if (key === "frontWall" || key === "interior") {
			if (activePreset === "interior-peek") setActivePreset("hero");
			if (interiorEditActive) setInteriorEditing(false);
		}
		onVisibilityChange?.({ ...visibility, [key]: !effectiveVisibility[key] });
	};

	const handleLayerPreset = (next: VisibilityFlags) => {
		// A preset decides the front wall and interior itself, so it leaves any
		// mode that forces them, the same way a manual toggle does.
		if (activePreset === "interior-peek") setActivePreset("hero");
		if (interiorEditActive) setInteriorEditing(false);
		onVisibilityChange?.(next);
	};

	useEffect(() => {
		setLockedMeasurement((current) =>
			resolveMeasurementReadout(current, buildInput, t) ? current : null,
		);
		// Clear stale measurement lock when dimensions change
	}, [
		buildInput.dimensions.width,
		buildInput.dimensions.length,
		buildInput.partitionSplit,
		buildInput.openings,
		buildInput,
		t,
	]);

	const isBlueprint = viewMode === "blueprint";
	// The framing/ventilation legend only makes sense for whatever layer is
	// actually on screen, so gate each swatch on the visibility flag that
	// reveals it. When none of those layers is active the whole legend section
	// drops out — no more permanent colour key for parts the user can't see.
	const contextualLegend = useMemo(() => {
		const entries: { key: string; color: string; label: string }[] = [];
		// Blue/red is the ventilation ANALYSIS coding — it only exists on
		// screen while that layer is on, so the key rides the same flag
		// (issue 123; it used to hang off `framing`, which never coloured
		// anything).
		if (visibility.ventilation) {
			entries.push(
				{ key: "supply", color: "#3b82f6", label: t.viewer_legendSupply },
				{ key: "exhaust", color: "#ef4444", label: t.viewer_legendExhaust },
			);
		}
		if (visibility.heaterClearance) {
			entries.push({
				key: "fireboard",
				color: "#9ca3af",
				label: t.viewer_legendFireboard,
			});
		}
		if (visibility.foundation) {
			entries.push(
				{ key: "beam", color: "#5a6b50", label: t.viewer_legendBeam },
				{ key: "pier", color: "#9ca3af", label: t.viewer_legendPier },
			);
		}
		return entries;
	}, [
		visibility.ventilation,
		visibility.heaterClearance,
		visibility.foundation,
		t,
	]);
	const timeOfDayLabel =
		timeOfDay < 0.35
			? t.viewer_todMidday
			: timeOfDay < 0.82
				? t.viewer_todGoldenHour
				: t.viewer_todNight;
	// A live drag owns the card while it lasts: its close button would dismiss a
	// readout the gesture is still writing to.
	const activeCardMeasurement =
		partitionDragPreview || interiorDragPreview ? null : lockedMeasurement;
	const benchInspectorOwnsReadout = !!onSelectBench &&
		(activeCardMeasurement?.kind === "bench" || activeCardMeasurement?.kind === "bench-edge");
	const activeMeasurementReadout = useMemo(() => {
		// An interior drag takes precedence so the card is reliably on screen for
		// the whole gesture, rather than only when the grab happened to have locked
		// a selection first.
		if (interiorDragPreview) {
			return resolveInteriorDragReadout(
				interiorDragPreview,
				buildInput,
				t,
				unitSystem,
			);
		}
		return partitionDragPreview
			? resolvePartitionPreviewReadout(
					partitionDragPreview,
					buildInput,
					t,
					unitSystem,
				)
			: resolveMeasurementReadout(
					lockedMeasurement ?? hoveredMeasurement?.target ?? null,
					buildInput,
					t,
					unitSystem,
				);
	}, [
		buildInput,
		hoveredMeasurement,
		interiorDragPreview,
		lockedMeasurement,
		partitionDragPreview,
		t,
		unitSystem,
	]);

	return (
		<div
			ref={rootRef}
			className={`viewer3d${sectionActive ? " viewer3d--section" : ""}`}
		>
			{/* ── R3F Canvas replaces the entire imperative setup ── */}
			<SceneCanvas
				key={canvasInstanceKey}
				buildInput={buildInput}
				visibility={effectiveVisibility}
				engine={engine}
				runtimeProfile={runtimeProfile}
				presentationProfile={presentationProfile}
				activePreset={activePresetObj}
				timeOfDay={timeOfDay}
				isTimeOfDayScrubbing={isTimeOfDayScrubbing}
				viewMode={viewMode}
				rotationDeg={rotationDeg}
				onRotationChange={onRotationChange}
				sectionActive={sectionActive}
				sectionAxis={sectionAxis}
				sectionPosition={sectionPosition}
				onSectionPositionChange={setSectionPosition}
				onSectionFamiliesChange={setSectionFamilies}
				isDraggingWindow={isDraggingWindow ?? null}
				showOpeningGuides={isEditingOpenings || !!isDraggingWindow}
				showScaleFigures={showScaleFigures}
				onScaleFigureCapacityChange={setScaleFigureCapacity}
				scaleFigureCapacityHint={scaleFigureCapacityHint}
				onWindowPlaced={onWindowPlaced}
				onOpeningMoved={onOpeningMoved}
				onPartitionSplitChange={onPartitionSplitChange}
				onDragCancelled={onDragCancelled}
				onMeasurementHoverChange={setHoveredMeasurement}
				onMeasurementSelectionChange={handleMeasurementSelection}
				measurementBadges={activeMeasurementReadout?.badges ?? []}
				measurementGuides={activeMeasurementReadout?.guides ?? []}
				onPartitionDragPreviewChange={setPartitionDragPreview}
				interiorEditing={interiorEditActive}
				onInteriorLayoutChange={onInteriorLayoutChange}
				onInteriorDragPreviewChange={setInteriorDragPreview}
				onInteriorEditAvailableChange={handleInteriorEditAvailable}
				onSnapshotReady={onSnapshotReady}
				onFrameReady={handleSceneFrameReady}
				onRendererReady={handleRendererReady}
				onRendererContextLost={handleRendererContextLost}
				onRendererContextRestored={handleRendererReady}
				onPerformanceChange={handlePerformanceChange}
				performanceMonitorResetKey={effectiveQualityOverride}
			/>

			{sceneLoadState !== "hidden" && (
				<SaunaSceneLoadingIndicator
					state={sceneLoadState}
					loadingLabel={t.viewer_loadingScene}
					readyLabel={t.viewer_sceneReady}
				/>
			)}

			{rendererState === "recovering" && (
				<div
					className="viewer-recovery-overlay"
					role="status"
					aria-live="polite"
				>
					<span className="viewer-recovery-chip">{t.viewer_recovering}</span>
				</div>
			)}

			{rendererState === "failed" && (
				<div className="viewer-recovery-banner" role="alert">
					<div className="viewer-recovery-banner__copy">
						<strong>{t.viewer_recoveryFailedTitle}</strong>
						<p>{t.viewer_recoveryFailedBody}</p>
					</div>
					<div className="viewer-recovery-banner__actions">
						<button
							type="button"
							className="viewer-recovery-btn viewer-recovery-btn--primary"
							onClick={handleRetryViewer}
						>
							{t.viewer_retry}
						</button>
						<button
							type="button"
							className="viewer-recovery-btn"
							onClick={handleReloadPage}
						>
							{t.viewer_reload}
						</button>
					</div>
				</div>
			)}

			{isDraggingWindow && onDragCancelled && (
				<ViewerPlacementBanner
					widthMm={isDraggingWindow.widthMm}
					heightMm={isDraggingWindow.heightMm}
					onCancel={onDragCancelled}
				/>
			)}

			{/* ── HUD: two semantic toolbar groups ── */}
			<div className="viewer-hud">
				<div
					className="viewer-hud-group viewer-hud-group--views"
					role="toolbar"
					aria-label={t.viewer_groupViews}
				>
					{cameraPresets.map((preset) => (
						<button
							key={preset.id}
							type="button"
							className={`viewer-camera-btn${activePreset === preset.id ? " active" : ""}`}
							onClick={() => handleCameraPresetClick(preset.id)}
							title={preset.label}
						>
							{preset.label}
						</button>
					))}
					{onInteriorLayoutChange && (
						/* Disabled in section view for the same reason rotation is: the cut freezes world
						   matrices, and a second pointer owner over the same canvas
						   would fight its handle. Disabled rather than hidden — a
						   control that vanishes reads as a bug. */
						<button
							type="button"
							className={`viewer-camera-btn viewer-interior-edit-toggle${interiorEditing ? " active" : ""}`}
							onClick={toggleInteriorEditing}
							// An ACTIVE toggle is never disabled — whatever the state, the
							// user must be able to leave the mode.
							disabled={
								!interiorEditing && (sectionActive || !!isDraggingWindow || !interiorEditAvailable)
							}
							title={
								interiorEditing
									? t.viewer_interiorEditOn
									: t.viewer_interiorEditOff
							}
							aria-label={
								interiorEditing
									? t.viewer_interiorEditOn
									: t.viewer_interiorEditOff
							}
							aria-pressed={interiorEditing}
						>
							<span>{interiorEditing ? t.interior_done : t.interior_edit}</span>
						</button>
					)}

				</div>

				{onRotationChange && (
					/* Paused in section view, exactly like the scene handle and the
					   measurement controller. The cut builds stencil shells that
					   freeze each mesh's world matrix (`matrixAutoUpdate = false`),
					   so turning the rig underneath them would leave the coloured
					   caps floating beside the building. Disabled rather than
					   hidden: the readout still answers "which way is it facing?",
					   and a control that vanishes reads as a bug. */
					<div
						className="viewer-hud-group viewer-hud-group--rotation"
						role="toolbar"
						aria-label={t.viewer_rotationGroup}
					>
						<button
							type="button"
							className="viewer-rotation-btn"
							onClick={() => onRotationChange(stepRotationDeg(rotationDeg, -1))}
							disabled={sectionActive}
							title={
								sectionActive
									? t.viewer_rotationPausedInSection
									: t.viewer_rotationLeft
							}
							aria-label={t.viewer_rotationLeft}
						>
							<RotateIcon direction="ccw" />
						</button>
						<output
							className="viewer-rotation-readout"
							aria-live="polite"
							aria-label={t.viewer_rotationValue.replaceAll(
								"{deg}",
								String(rotationDeg),
							)}
						>
							{`${rotationDeg}°`}
						</output>
						<button
							type="button"
							className="viewer-rotation-btn"
							onClick={() => onRotationChange(stepRotationDeg(rotationDeg, 1))}
							disabled={sectionActive}
							title={
								sectionActive
									? t.viewer_rotationPausedInSection
									: t.viewer_rotationRight
							}
							aria-label={t.viewer_rotationRight}
						>
							<RotateIcon direction="cw" />
						</button>
					</div>
				)}

				{onVisibilityChange && (
					<VisibilityBar
						visibility={effectiveVisibility}
						onToggle={handleLayerToggle}
						onApply={handleLayerPreset}
					/>
				)}
				<details name="viewer-tools" className="viewer-hud-stack viewer-display-disclosure">
					<summary>{t.viewer_groupDisplay}</summary>
					<div
						className="viewer-hud-group viewer-hud-group--display"
						role="toolbar"
						aria-label={t.viewer_groupDisplay}
					>
						<label className="viewer-tod-slider">
							<span className="viewer-tod-slider__header">
								<span className="viewer-tod-slider__label">
									{t.viewer_todLabel}
								</span>
								<span className="viewer-tod-slider__value">
									{timeOfDayLabel}
								</span>
							</span>
							<input
								type="range"
								min="0"
								max="1"
								step="0.01"
								value={timeOfDay}
								onChange={handleTimeOfDayInput}
								onPointerDown={() => setIsTimeOfDayScrubbing(true)}
								onPointerUp={stopTimeOfDayScrubbing}
								onPointerCancel={stopTimeOfDayScrubbing}
								onKeyDown={() => setIsTimeOfDayScrubbing(true)}
								onKeyUp={stopTimeOfDayScrubbing}
								onBlur={stopTimeOfDayScrubbing}
								aria-label={t.viewer_todAriaLabel}
								aria-valuetext={timeOfDayLabel}
							/>
							<span className="viewer-tod-slider__ticks" aria-hidden="true">
								<span>{t.viewer_todMidday}</span>
								<span>{t.viewer_todGoldenHour}</span>
								<span>{t.viewer_todNight}</span>
							</span>
						</label>

						<button
							type="button"
							className={`viewer-blueprint-toggle${isBlueprint ? " active" : ""}`}
							onClick={toggleViewMode}
							title={isBlueprint ? t.viewer_blueprintOn : t.viewer_blueprintOff}
							aria-label={
								isBlueprint ? t.viewer_blueprintOn : t.viewer_blueprintOff
							}
							aria-pressed={isBlueprint}
						>
							<BlueprintIcon />
						</button>

						<button
							type="button"
							className={`viewer-section-toggle${sectionActive ? " active" : ""}`}
							onClick={toggleSection}
							title={
								sectionActive
									? t.viewer_sectionToggleOn
									: t.viewer_sectionToggleOff
							}
							aria-label={
								sectionActive
									? t.viewer_sectionToggleOn
									: t.viewer_sectionToggleOff
							}
							aria-pressed={sectionActive}
						>
							<SectionIcon />
						</button>

						<button
							type="button"
							className={`viewer-scale-figures-toggle${showScaleFigures ? " active" : ""}`}
							onClick={toggleScaleFigures}
							title={
								showScaleFigures
									? t.viewer_scaleFiguresOn
									: t.viewer_scaleFiguresOff
							}
							aria-label={
								showScaleFigures
									? t.viewer_scaleFiguresOn
									: t.viewer_scaleFiguresOff
							}
							aria-pressed={showScaleFigures}
						>
							<ScaleFigureIcon />
						</button>

						{isAmbienceSupported && (
							<button
								type="button"
								className={`viewer-ambience-toggle${isAmbienceOn ? " active" : ""}`}
								onClick={toggleAmbience}
								title={
									isAmbienceOn ? t.viewer_ambienceOn : t.viewer_ambienceOff
								}
								aria-label={
									isAmbienceOn ? t.viewer_ambienceOn : t.viewer_ambienceOff
								}
								aria-pressed={isAmbienceOn}
							>
								<SoundIcon muted={!isAmbienceOn} />
							</button>
						)}

						<button
							type="button"
							className={`viewer-cinema-toggle${presentationMode === "cinematic" ? " active" : ""}`}
							onClick={togglePresentationMode}
							title={
								presentationMode === "cinematic"
									? "Cinematic on — softer shadows, deeper bokeh"
									: "Cinematic off"
							}
							aria-label="Cinematic preset"
							aria-pressed={presentationMode === "cinematic"}
						>
							<CinemaIcon />
						</button>

						<div className="viewer-quality-wrapper">
							<button
								type="button"
								className={`viewer-quality-toggle${showQualityPanel ? " active" : ""}`}
								onClick={() => setShowQualityPanel((v) => !v)}
								title={t.viewer_qualityButton}
								aria-label={t.viewer_qualityButton}
								aria-expanded={showQualityPanel}
							>
								⚙
							</button>
							{showQualityPanel && (
								<div className="viewer-quality-panel" role="menu">
									<p className="viewer-quality-label">
										{t.viewer_qualityLabel}
									</p>
									{(
										["auto", "high", "balanced", "low"] as ViewerQualityTier[]
									).map((tier) => (
										<button
											key={tier}
											type="button"
											role="menuitemradio"
											aria-checked={qualityOverride === tier}
											className={`viewer-quality-btn${qualityOverride === tier ? " active" : ""}`}
											onClick={() => {
												setForcedQualityOverride(null);
												setQualityOverride(tier);
												setAdaptiveQualityTier(null);
												setShowQualityPanel(false);
											}}
										>
											{tier === "auto"
												? `${t.viewer_qualityAuto} · ${effectiveQualityLabel}`
												: tier === "high"
													? t.viewer_qualityHigh
													: tier === "balanced"
														? t.viewer_qualityBalanced
														: t.viewer_qualityLow}
										</button>
									))}
								</div>
							)}
						</div>
					</div>

					{showScaleFigures && scaleFigureCapacity && (
						<p className="viewer-capacity-chip">
							<span className="viewer-capacity-chip__seats">
								{(scaleFigureCapacity.seats === 1
									? t.viewer_scaleFiguresSeat
									: t.viewer_scaleFiguresSeats
								).replace("{count}", String(scaleFigureCapacity.seats))}
							</span>
							{scaleFigureCapacity.relaxSeats > 0 && (
								<span className="viewer-capacity-chip__relax">
									{t.viewer_scaleFiguresRelaxSeats.replace(
										"{count}",
										String(scaleFigureCapacity.relaxSeats),
									)}
								</span>
							)}
						</p>
					)}
				</details>
			</div>

			{/* ── Help pill (controls hint + framing legend co-located) ── */}
			<div className={`viewer-help${isHelpOpen ? " is-open" : ""}`}>
				<button
					type="button"
					className="viewer-help-toggle"
					onClick={toggleHelp}
					aria-label={t.viewer_helpToggle}
					aria-expanded={isHelpOpen}
				>
					<HelpIcon />
				</button>
				{isHelpOpen && (
					<div
						className="viewer-help-body"
						role="region"
						aria-label={t.viewer_helpToggle}
					>
						<section className="viewer-help-section">
							<h4 className="viewer-help-heading">
								{t.viewer_helpControlsTitle}
							</h4>
							<ul className="viewer-help-list">
								<li>{t.viewer_hintDrag}</li>
								<li>{t.viewer_hintScroll}</li>
								<li>{t.viewer_hintPan}</li>
							</ul>
						</section>
						{contextualLegend.length > 0 && (
							<section className="viewer-help-section">
								<h4 className="viewer-help-heading">
									{t.viewer_helpLegendTitle}
								</h4>
								<div className="viewer-legend-body">
									{contextualLegend.map((entry) => (
										<div key={entry.key} className="viewer-legend-item">
											<span
												className="viewer-legend-swatch"
												style={{ background: entry.color }}
											/>
											{entry.label}
										</div>
									))}
								</div>
							</section>
						)}
					</div>
				)}
			</div>

			{sectionActive && (
				<section
					className="viewer-section-panel"
					aria-label={t.viewer_sectionLegendTitle}
				>
					<div
						className="viewer-section-panel__axis"
						role="group"
						aria-label={t.viewer_sectionAxisLabel}
					>
						<button
							type="button"
							className={`viewer-section-axis-btn${sectionAxis === "x" ? " active" : ""}`}
							onClick={() => handleSectionAxisChange("x")}
							aria-pressed={sectionAxis === "x"}
						>
							{t.viewer_sectionAxisWidth}
						</button>
						<button
							type="button"
							className={`viewer-section-axis-btn${sectionAxis === "z" ? " active" : ""}`}
							onClick={() => handleSectionAxisChange("z")}
							aria-pressed={sectionAxis === "z"}
						>
							{t.viewer_sectionAxisLength}
						</button>
					</div>

					<label className="viewer-section-panel__slider">
						<span className="viewer-section-panel__slider-label">
							{t.viewer_sectionPositionLabel}
						</span>
						<input
							type="range"
							min="0"
							max="1"
							step="0.01"
							value={sectionPosition}
							onChange={handleSectionPositionInput}
							aria-label={t.viewer_sectionPositionLabel}
						/>
					</label>

					<div className="viewer-section-panel__info">
						<button
							type="button"
							className="viewer-section-panel__info-toggle"
							onClick={() => setIsSectionInfoOpen((open) => !open)}
							aria-expanded={isSectionInfoOpen}
						>
							<span>{t.viewer_sectionLegendTitle}</span>
							<ChevronIcon className="viewer-section-panel__info-chevron" />
						</button>

						{isSectionInfoOpen && (
							<div className="viewer-section-panel__info-body">
								{sectionLegendEntries.length > 0 && (
									<div className="viewer-legend-body">
										{sectionLegendEntries.map((entry) => (
											<div key={entry.family} className="viewer-legend-item">
												<span
													className="viewer-legend-swatch viewer-legend-swatch--square"
													style={{ background: hexColor(entry.capColor) }}
												/>
												{t[entry.legendKey]}
											</div>
										))}
									</div>
								)}

								<p className="viewer-section-panel__note">
									{t.viewer_sectionMeasurementPaused}
								</p>
							</div>
						)}
					</div>
				</section>
			)}

			{activeMeasurementReadout && !benchInspectorOwnsReadout && (
				<section className="viewer-measure-card" aria-live="polite">
					<div className="viewer-measure-card__header">
						<div>
							<p className="viewer-measure-card__eyebrow">
								{activeMeasurementReadout.title}
							</p>
							<h3>{activeMeasurementReadout.subtitle}</h3>
						</div>
						{activeCardMeasurement && (
							<button
								type="button"
								className="viewer-measure-card__close"
								onClick={() => setLockedMeasurement(null)}
								aria-label={t.common_close}
							>
								✕
							</button>
						)}
					</div>
					<dl className="viewer-measure-card__metrics">
						{activeMeasurementReadout.metrics.map((metric) => (
							<div
								key={`${metric.label}-${metric.value}`}
								className="viewer-measure-card__metric"
							>
								<dt>{metric.label}</dt>
								<dd>{metric.value}</dd>
							</div>
						))}
					</dl>
					{activeMeasurementReadout.note && (
						<p
							className={`viewer-measure-card__note${
								activeMeasurementReadout.note.invalid ? " is-invalid" : ""
							}`}
							role={activeMeasurementReadout.note.invalid ? "alert" : undefined}
							data-testid="viewer-measure-note"
						>
							{activeMeasurementReadout.note.text}
						</p>
					)}
				</section>
			)}
		</div>
	);
}
